import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Printer, IdCard, FileSignature, ShieldAlert, LogOut } from "lucide-react";
import { toast } from "sonner";
import { isoToLocal, localToIso, openPdfPath } from "./wardUtils";

const SUPPORTS = [["oxygen", "ऑक्सीजन (Oxygen)"], ["cpap", "CPAP"], ["hfnc", "HHFNC"], ["ventilator", "वेंटिलेटर (Ventilator)"]];

export function PrintDocsMenu({ bill }) {
  const [consent, setConsent] = useState(false);
  const base = `/ward/bills/${bill.id}/docs`;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" data-testid="ward-print-docs"><Printer size={15} className="mr-1" /> Print docs</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => openPdfPath(`${base}/id-ticket`)} data-testid="ward-doc-id-ticket"><IdCard size={14} className="mr-2" /> ID ticket</DropdownMenuItem>
          <DropdownMenuItem onClick={() => openPdfPath(`${base}/acknowledgement`)} data-testid="ward-doc-ack"><FileSignature size={14} className="mr-2" /> Acknowledgement slip</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setConsent(true)} className="text-destructive" data-testid="ward-doc-consent"><ShieldAlert size={14} className="mr-2" /> High-risk consent (Hindi)</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {consent && <ConsentDialog bill={bill} onClose={() => setConsent(false)} onPrint={(qs) => { openPdfPath(`${base}/high-risk-consent${qs}`); setConsent(false); }} />}
    </>
  );
}

function ConsentDialog({ bill, onClose, onPrint }) {
  const [diagnosis, setDiagnosis] = useState(bill.diagnosis || "");
  const [supports, setSupports] = useState(new Set());
  const toggle = (k) => setSupports((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const print = () => {
    const p = new URLSearchParams();
    if (diagnosis.trim()) p.set("diagnosis", diagnosis.trim());
    if (supports.size) p.set("supports", [...supports].join(","));
    onPrint(p.toString() ? `?${p}` : "");
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="ward-consent-modal">
        <DialogHeader>
          <DialogTitle className="text-destructive flex items-center gap-2"><ShieldAlert size={16} /> High-risk consent (Hindi)</DialogTitle>
          <DialogDescription>Fills the diagnosis and support onto the printed Hindi consent. The bill is not modified.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Diagnosis (as briefed to parents)</Label><Textarea rows={2} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} data-testid="ward-consent-diagnosis" /></div>
          <div><Label>Support being provided</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SUPPORTS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => toggle(k)} data-testid={`ward-consent-support-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${supports.has(k) ? "bg-destructive/10 border-destructive/40 text-destructive" : "border-border text-muted-foreground hover:bg-muted"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={print} data-testid="ward-consent-print">Print consent</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DischargeDialog({ bill, onClose, onDone }) {
  const [at, setAt] = useState(isoToLocal(new Date().toISOString()));
  const [type, setType] = useState("Normal");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/ward/bills/${bill.id}/discharge`, { discharge_at: localToIso(at), discharge_type: type });
      toast.success(`${bill.patient_name} discharged`);
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Discharge failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ward-discharge-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LogOut size={16} /> Discharge {bill.patient_name}</DialogTitle>
          <DialogDescription>The bill stays editable after discharge.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Discharge date & time</Label><Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} data-testid="ward-discharge-datetime" /></div>
          <div className="space-y-1.5"><Label>Discharge type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="ward-discharge-type">
              {["Normal", "LAMA", "Referred", "DOR", "Death"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !at} data-testid="ward-discharge-confirm">Confirm discharge</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
